import { Button, Collapse, message } from "antd";
import DisplayNameListPopover from "components/bulk-edit-material/display-name-list";
import WorkflowTemplateBulkEditor from "components/workflow-template-bulk-editor";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { useContext, useEffect, useMemo, useState } from "react";
import { FeatureTypes } from "entity-app/constants";
import { APPLY_WORKFLOW_MUTATION } from "entity-app/shared-components/entity-dateblock/queries/mutation";

const { Panel } = Collapse;

function transformToDateBlockData(feature: any): any {
  const formattedFeature: any = {
    id: feature.id,
    title: feature.title,
    float: feature.planned_float || feature.pre_WF_start_float || 0,
    workflow_status: feature.workflow_status.code
  };

  const dbData = feature.date_block?.milestones.map((milestone: any) => ({
    id: milestone.id,
    name: milestone.name,
    milestone_action: milestone.status,
    offsetName: milestone.offset_name,
    sequence_no: milestone.sequence_no,
    value: milestone.offset_duration || 0,
    workflow_template_offset_id: milestone.offset_id
  }));

  formattedFeature.date_block = dbData;

  return formattedFeature;
}

function getInstanceOffsetId(sequenceNo: any, feature: any) {
  const foundMilestone = feature.date_block.milestones.find(
    (milestone: any) => milestone.sequence_no === sequenceNo
  );

  return foundMilestone?.offset_id;
}

const labelsByFeatureTypeId: any = {
  [FeatureTypes.BID_PACKAGE]: {
    panelHeaderFeatureName: "Bid Packages"
  },
  [FeatureTypes.SUBMITTALS]: {
    panelHeaderFeatureName: "Submittals"
  },
  [FeatureTypes.MATERIALS]: {
    panelHeaderFeatureName: "Materials"
  }
};

function PanelItemNewModel(props: {
  template: any;
  featureTypeId: FeatureTypes;
  onSaveSuccess: () => void;
}) {
  const { template, featureTypeId, onSaveSuccess } = props;

  const { gqlClientForProject }: TProjectContext = useContext(ProjectContext);

  const [mutationApplyWorkflowInstanceDataChanges] = useCIQMutation(
    APPLY_WORKFLOW_MUTATION,
    {
      client: gqlClientForProject
    }
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [milestones, setMilestones] = useState<any[]>([]);
  const [float, setFloat] = useState(null);

  const [isInitialDataApplied, setIsInitialDataApplied] = useState(false);

  const featuresData = useMemo(() => {
    return template.features.map((feature: any) =>
      transformToDateBlockData(feature)
    );
  }, [template.features]);

  const incomingSameFloatValueAcrossFeatures = useMemo(() => {
    if (!featuresData.length) return "";
    let hasSameValues = true;
    const firstFloatValue = featuresData[0].float;
    featuresData.forEach((feature: any) => {
      const floatValue = feature.float;
      if (floatValue !== firstFloatValue) {
        hasSameValues = false;
      }
    });
    if (hasSameValues) {
      return firstFloatValue;
    }

    return null;
  }, [featuresData]);

  const incomingDateBlockData = useMemo(() => {
    if (!featuresData.length) return [];
    const DBmilestones = featuresData[0].date_block.map(
      (milestone: any, index: number) => {
        let hasSameValues = true;
        const firstFeatureOffsetValue = milestone.value;
        featuresData.forEach((feature: any) => {
          const offsetValue = feature.date_block[index].value;
          if (offsetValue !== firstFeatureOffsetValue) {
            hasSameValues = false;
          }
        });

        const milestoneToReturn = { ...milestone };

        if (hasSameValues) {
          milestoneToReturn.value = firstFeatureOffsetValue;
        } else {
          milestoneToReturn.value = null;
        }

        return milestoneToReturn;
      }
    );
    return DBmilestones.sort((a: any, b: any) => a.sequence_no - b.sequence_no);
  }, [featuresData]);

  useEffect(() => {
    if (!incomingDateBlockData.length || isInitialDataApplied) return;

    setMilestones(incomingDateBlockData);

    if (incomingSameFloatValueAcrossFeatures) {
      setFloat(incomingSameFloatValueAcrossFeatures);
    }

    setIsInitialDataApplied(true);
  }, [
    incomingDateBlockData,
    incomingSameFloatValueAcrossFeatures,
    isInitialDataApplied
  ]);

  const exitEditAfterSave = () => {
    setIsEditing(false);
  };

  const onCancelEdit = () => {
    setMilestones(incomingDateBlockData);
    setFloat(incomingSameFloatValueAcrossFeatures);
    setIsEditing(false);
  };

  const onSave = async (event: any) => {
    setIsSaving(true);
    event.stopPropagation();

    const filteredMilestones = milestones?.filter(
      (milestone: any) =>
        milestone.workflow_template_offset_id && milestone.value !== null
    );

    const payloadInput: any[] = [];
    template.features.forEach((feature: any) => {
      const objectToPush: any = {
        feature_instance_id: feature.id,
        workflow_instance_id: feature.workflowInstance.id,
        offsets: [],
        pre_WF_start_float: float
      };

      filteredMilestones.forEach((milestone: any) => {
        objectToPush.offsets.push({
          id: getInstanceOffsetId(milestone.sequence_no, feature),
          duration: milestone.value
        });
      });

      payloadInput.push(objectToPush);
    });

    const response = await mutationApplyWorkflowInstanceDataChanges({
      variables: {
        input: payloadInput,
        metadata: {
          request_source: "BULK_EDIT_DURATION"
        }
      }
    });

    if (response.success) {
      exitEditAfterSave();
      message.success("Durations updated successfully");
      onSaveSuccess();
    }

    setIsSaving(false);
  };

  const panelHeader = (
    <div
      className="flex items-center justify-between"
      data-testid="panelitemnewmodel-header"
    >
      <div
        className="uppercase !text-gray-600"
        data-testid="panelitemnewmodel-title"
      >
        {template.name} (
        <span className="-ml-1" data-testid="panelitemnewmodel-feature-count">
          <DisplayNameListPopover
            list={template.features.map((feature: any) => {
              return { name: feature.title };
            })}
          >
            {template.features.length}
          </DisplayNameListPopover>
        </span>{" "}
        <span className="-ml-1.5" data-testid="panelitemnewmodel-feature-type">
          {labelsByFeatureTypeId[featureTypeId].panelHeaderFeatureName}
        </span>
        )
      </div>
      <div data-testid="panelitemnewmodel-actions">
        {!isEditing && (
          <Button
            type="primary"
            size="small"
            className="px-4"
            onClick={(event) => {
              event.stopPropagation();
              setIsEditing(true);
            }}
            data-testid="panelitemnewmodel-button-edit"
          >
            Edit
          </Button>
        )}
        {(isEditing || isSaving) && (
          <div
            className="space-x-3"
            data-testid="panelitemnewmodel-edit-actions"
          >
            <Button
              type="primary"
              size="small"
              className="px-4"
              onClick={(event) => {
                onSave(event);
              }}
              disabled={isSaving}
              loading={isSaving}
              data-testid="panelitemnewmodel-button-save"
            >
              Save
            </Button>
            <Button
              size="small"
              className="px-4"
              onClick={(event) => {
                event.stopPropagation();
                onCancelEdit();
              }}
              disabled={isSaving}
              data-testid="panelitemnewmodel-button-cancel"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  let panelProps = {
    ...props
  };
  if (isEditing) {
    panelProps = {
      ...panelProps
    };
  }

  return (
    <Panel
      {...panelProps}
      header={panelHeader}
      key={template.id}
      data-testid="panelitemnewmodel-panel"
    >
      <WorkflowTemplateBulkEditor
        disabled={!isEditing}
        milestonesState={[milestones, setMilestones]}
        floatState={[float, setFloat]}
        featureType={featureTypeId}
        data-testid="panelitemnewmodel-bulk-editor"
      />
    </Panel>
  );
}

export default PanelItemNewModel;
